import {
  IntegrationFormField,
  useIntegrationFormContext,
} from 'modules/mobileApps/context/integrationForm';
import {
  IntegrationOperationType,
  useIntegrationMetadataContext,
} from 'modules/mobileApps/context/integrationMetadata';
import { ModalType, useModalContext } from 'modules/mobileApps/context/modal';
import {
  AppPlatform,
  IntegrationAuthType,
  IntegrationPlatform,
  IntegrationModalView as ModalView,
} from 'modules/mobileApps/types';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BUTTON_SIZES, BUTTON_VARIANTS, Button } from 'ui/button';
import {
  MODAL_SIZES,
  MODAL_VARIANTS,
  Modal,
  ModalBackButton,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'ui/modal';
import {
  getIsCreateOrUpdateIntegrationError,
  getIsCreateOrUpdateIntegrationInProgress,
} from '../../selectors';
import { ApnsIntegrationForm } from '../IntegrationForm/ApnsIntegrationForm';
import { determineCreateIntegrationRequests, determineIntegrationRequest } from '../utils';
import { formatIntegrationFieldsForSave, getIsModalOpen } from './utils';

export const CONFIGURE_IOS_INTEGRATION_MODAL_ID = 'configure-ios-integration-modal';

export const ConfigureIosIntegrationModal = () => {
  const dispatch = useDispatch();

  const {
    appDetails,
    assignedIntegrations,
    operationType,
    selectedIntegration,
    setSelectedIntegration,
  } = useIntegrationMetadataContext();
  const { closeModal, openedModals } = useModalContext();
  const { getValues, handleSubmit, setValue, watch } = useIntegrationFormContext();

  const authType = watch(IntegrationFormField.authType);
  const isCreateOperation = operationType === IntegrationOperationType.CREATE;

  const [modalView, setModalView] = useState(ModalView.AUTH_SELECT);
  const [isModalDisabled, setIsModalDisabled] = useState(true);

  const createOrUpdateInProgress = useSelector(getIsCreateOrUpdateIntegrationInProgress);
  const createOrUpdateError = useSelector(getIsCreateOrUpdateIntegrationError);

  const handleBackButton = () => {
    setModalView(ModalView.AUTH_SELECT);
    setValue(IntegrationFormField.authType, undefined);
  };

  const handleContinue = () => {
    if (selectedIntegration && isCreateOperation) {
      const isFirebase = authType === IntegrationAuthType.FIREBASE_JSON;
      setSelectedIntegration({
        ...selectedIntegration,
        platform: isFirebase ? IntegrationPlatform.GCM : IntegrationPlatform.APNS,
      });
    }

    setModalView(ModalView.AUTH_DETAILS);
  };

  const handleClose = () => {
    setValue(IntegrationFormField.authType, undefined);
    closeModal(ModalType.ConfigureIosIntegrationModal);
  };

  const handleSave = handleSubmit(async () => {
    if (!appDetails || !selectedIntegration) return;

    const formFields = getValues();
    const formattedIntegration = await formatIntegrationFieldsForSave(
      formFields,
      selectedIntegration,
      operationType,
      AppPlatform.IOS,
    );

    if (operationType === IntegrationOperationType.CREATE) {
      const dispatchRequests = determineCreateIntegrationRequests(
        { ...formattedIntegration, mobileAppId: appDetails.id },
        assignedIntegrations,
      );
      dispatchRequests.forEach((request) => dispatch(request));
    } else {
      dispatch(determineIntegrationRequest(formattedIntegration, assignedIntegrations));
    }
  });

  useEffect(() => {
    if (modalView === ModalView.AUTH_SELECT) setIsModalDisabled(!authType);
  }, [authType]);

  // Only close modal if create/update attempt is complete and there is no error
  useEffect(() => {
    if (modalView === ModalView.AUTH_DETAILS && !createOrUpdateInProgress && !createOrUpdateError) {
      handleClose();
    }
  }, [createOrUpdateInProgress]);

  return (
    <Modal
      data-test={CONFIGURE_IOS_INTEGRATION_MODAL_ID}
      id="mobileApps-createUpdateIntegrationModal"
      isModalOpen={getIsModalOpen(openedModals, ModalType.ConfigureIosIntegrationModal)}
      onClose={handleClose}
      size={MODAL_SIZES.MEDIUM}
      variant={MODAL_VARIANTS.DEFAULT}
    >
      <ModalHeader data-test="integration-modal-header">
        {modalView === ModalView.AUTH_SELECT ? (
          'Integration credentials'
        ) : (
          <ModalBackButton onClick={handleBackButton}>Back</ModalBackButton>
        )}
      </ModalHeader>
      <ModalBody data-test="integration-modal-body">
        <ApnsIntegrationForm handleContinue={handleContinue} modalView={modalView} />
      </ModalBody>
      <ModalFooter data-test="integration-modal-actions">
        {modalView === ModalView.AUTH_SELECT ? (
          <Button
            data-test="action-continue"
            isDisabled={isModalDisabled}
            onClick={handleContinue}
            size={BUTTON_SIZES.LARGE}
            variant={BUTTON_VARIANTS.PRIMARY}
          >
            Continue
          </Button>
        ) : (
          <Button
            data-test="action-update"
            onClick={handleSave}
            size={BUTTON_SIZES.LARGE}
            variant={BUTTON_VARIANTS.PRIMARY}
          >
            Add credential
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};
